/** Billing rules the page needs, kept out of the components. */
import type { BillingInterval } from "@sp/shared/enums";
import type { Billing, Plan, Price } from "./api/types";

/** A live paid subscription changes plan in place; anything else goes through checkout. */
export const hasLiveSubscription = (b: Billing) =>
  b.subscription?.provider === "dodo" && (b.subscription.status === "active" || b.subscription.status === "past_due");

/** The plan's price for the period; one that can be bought wins over one that can't (not yet linked to the provider). */
export const priceFor = (plan: Plan, interval: BillingInterval): Price | undefined => {
  const forInterval = plan.prices.filter((p) => p.interval === interval);
  return forInterval.find((p) => p.purchasable) ?? forInterval[0];
};

export const isCurrentPrice = (b: Billing, price: Price | undefined) => !!price && b.subscription?.price?.id === price.id;

export const STATUS_TEXT: Record<string, string> = {
  trialing: "trial",
  active: "active",
  past_due: "payment failed",
  expired: "expired",
};

/** Months in each billing period (mirrors BILLING_INTERVAL_MONTHS, which lives beside zod in @sp/shared/enums). */
export const INTERVAL_MONTHS: Record<BillingInterval, number> = { month: 1, year: 12 };

export const INTERVAL_LABEL: Record<BillingInterval, string> = { month: "monthly", year: "yearly" };

/** "every month", "every year" */
export const billedEvery = (i: BillingInterval) => `every ${i}`;

/** What the price works out to per month, in minor units. */
export const perMonthMinor = (price: Pick<Price, "amountMinor" | "interval">) => Math.round(price.amountMinor / INTERVAL_MONTHS[price.interval]);

/** Whole-percent saving of this price over paying monthly for the same plan; 0 when there is nothing to compare. */
export function savingPercent(plan: Plan, price: Price): number {
  const monthly = priceFor(plan, "month");
  if (!monthly || price.interval === "month" || monthly.currency !== price.currency) return 0;
  const full = monthly.amountMinor * INTERVAL_MONTHS[price.interval];
  return full > 0 ? Math.round((1 - price.amountMinor / full) * 100) : 0;
}

/** Whole months this price saves over paying monthly ("2 months free"), or 0 when the saving isn't a whole number of months. */
export function monthsFree(plan: Plan, price: Price): number {
  const monthly = priceFor(plan, "month");
  if (!monthly || price.interval === "month" || monthly.currency !== price.currency || monthly.amountMinor <= 0) return 0;
  const saved = monthly.amountMinor * INTERVAL_MONTHS[price.interval] - price.amountMinor;
  return saved > 0 && saved % monthly.amountMinor === 0 ? saved / monthly.amountMinor : 0;
}

/**
 * The offer to show on the period switch, e.g. "2 months free" on "yearly": the smallest saving any plan gets on that
 * period, so the label is true for every plan. Null when some plan saves nothing.
 */
export function intervalOffer(plans: Plan[], interval: BillingInterval): string | null {
  if (interval === "month" || !plans.length) return null;
  const offers = plans.map((p) => {
    const price = priceFor(p, interval);
    return price ? { free: monthsFree(p, price), saving: savingPercent(p, price) } : { free: 0, saving: 0 };
  });
  const free = Math.min(...offers.map((o) => o.free));
  if (free > 0) return `${free} ${free === 1 ? "month" : "months"} free`;
  const saving = Math.min(...offers.map((o) => o.saving));
  return saving > 0 ? `save ${saving}%` : null;
}

export function intervalsOffered(plans: Plan[]): BillingInterval[] {
  const set = new Set(plans.flatMap((p) => p.prices.map((x) => x.interval)));
  return (Object.keys(INTERVAL_MONTHS) as BillingInterval[]).filter((i) => set.has(i));
}
